import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import { useGetBrandsQuery, useGetCigarLinesQuery } from '@store/api/cigarApi'
import {
  useCreateCigarLineMutation,
  useDeleteCigarLineMutation,
  useUpdateCigarLineMutation,
} from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const CigarLineManagement: React.FC = () => {
  const [brandFilterId, setBrandFilterId] = useState('')
  const [brandId, setBrandId] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [strengthLevel, setStrengthLevel] = useState('')
  const [wrapperType, setWrapperType] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStrength, setEditStrength] = useState('')
  const [editWrapper, setEditWrapper] = useState('')

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteLabel, setDeleteLabel] = useState('')

  const { data: brands = [], isLoading: brandsLoading } = useGetBrandsQuery({})
  const listBrandId = brandFilterId || undefined
  const { data: cigarLines = [], isLoading: linesLoading, refetch } = useGetCigarLinesQuery(
    { brandId: listBrandId },
    { skip: !listBrandId }
  )

  const [createCigarLine, { isLoading: isCreating }] = useCreateCigarLineMutation()
  const [updateCigarLine, { isLoading: isUpdating }] = useUpdateCigarLineMutation()
  const [deleteCigarLine, { isLoading: isDeleting }] = useDeleteCigarLineMutation()

  const sortedLines = useMemo(
    () => [...cigarLines].sort((a, b) => a.name.localeCompare(b.name)),
    [cigarLines]
  )

  const showApiError = (err: unknown, fallback: string) => {
    const apiErr = err as { data?: { message?: string } }
    setMessage({ type: 'error', text: apiErr?.data?.message ?? fallback })
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    if (!brandId) {
      setMessage({ type: 'error', text: 'Select a brand' })
      return
    }
    try {
      await createCigarLine({
        brandId,
        name: name.trim(),
        description: description.trim() || undefined,
        strengthLevel: strengthLevel.trim() || undefined,
        wrapperType: wrapperType.trim() || undefined,
      }).unwrap()
      setName('')
      setDescription('')
      setStrengthLevel('')
      setWrapperType('')
      setMessage({ type: 'success', text: 'Cigar line created' })
      if (brandFilterId !== brandId) setBrandFilterId(brandId)
      refetch()
    } catch (err: unknown) {
      showApiError(err, 'Failed to create cigar line')
    }
  }

  const openEdit = (line: (typeof cigarLines)[0]) => {
    setEditId(line.id)
    setEditName(line.name)
    setEditDescription(line.description ?? '')
    setEditStrength(line.strengthLevel ?? '')
    setEditWrapper(line.wrapperType ?? '')
    setEditOpen(true)
  }

  const handleEditSave = async () => {
    if (!editId || !editName.trim()) return
    setMessage(null)
    try {
      await updateCigarLine({
        id: editId,
        updates: {
          name: editName.trim(),
          description: editDescription.trim() || undefined,
          strengthLevel: editStrength.trim() || undefined,
          wrapperType: editWrapper.trim() || undefined,
        },
      }).unwrap()
      setEditOpen(false)
      setMessage({ type: 'success', text: 'Cigar line updated' })
      refetch()
    } catch (err: unknown) {
      showApiError(err, 'Failed to update cigar line')
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setMessage(null)
    try {
      await deleteCigarLine(deleteId).unwrap()
      setDeleteId(null)
      setMessage({ type: 'success', text: 'Cigar line deleted' })
      refetch()
    } catch (err: unknown) {
      showApiError(err, 'Failed to delete cigar line')
    }
  }

  return (
    <AdminPageGate permission="content:write" title="Cigar Lines">
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Cigar Line Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage shared catalog cigar lines by brand for search, imports, and submissions.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Cigar Line
          </Typography>
          <Box component="form" onSubmit={handleCreate}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth required disabled={brandsLoading}>
                  <InputLabel id="create-line-brand-label">Brand</InputLabel>
                  <Select
                    labelId="create-line-brand-label"
                    label="Brand"
                    value={brandId}
                    onChange={(e) => setBrandId(e.target.value)}
                  >
                    {brands.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Line name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Stack>
              <TextField
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Strength (optional)"
                  value={strengthLevel}
                  onChange={(e) => setStrengthLevel(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Wrapper (optional)"
                  value={wrapperType}
                  onChange={(e) => setWrapperType(e.target.value)}
                  fullWidth
                />
              </Stack>
              <Box>
                <Button type="submit" variant="contained" disabled={isCreating || !name.trim() || !brandId}>
                  {isCreating ? 'Creating…' : 'Create'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">
              Lines {listBrandId ? `(${sortedLines.length})` : ''}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={brandsLoading}>
              <InputLabel id="filter-brand-label">Filter by brand</InputLabel>
              <Select
                labelId="filter-brand-label"
                label="Filter by brand"
                value={brandFilterId}
                onChange={(e) => setBrandFilterId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a brand</em>
                </MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {!listBrandId ? (
            <Typography variant="body2" color="text.secondary">
              Select a brand to view and manage its cigar lines.
            </Typography>
          ) : linesLoading ? (
            <CircularProgress size={28} />
          ) : sortedLines.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No cigar lines for this brand yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Strength</TableCell>
                    <TableCell>Wrapper</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedLines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.name}</TableCell>
                      <TableCell>{line.strengthLevel ?? '—'}</TableCell>
                      <TableCell>{line.wrapperType ?? '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label={`Edit ${line.name}`}
                          size="small"
                          onClick={() => openEdit(line)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label={`Delete ${line.name}`}
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteId(line.id)
                            setDeleteLabel(line.name)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit cigar line</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Line name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Strength"
              value={editStrength}
              onChange={(e) => setEditStrength(e.target.value)}
              fullWidth
            />
            <TextField
              label="Wrapper"
              value={editWrapper}
              onChange={(e) => setEditWrapper(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={isUpdating || !editName.trim()}>
            {isUpdating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete cigar line?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteLabel}</strong> from the catalog? This may fail if cigars still reference it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default CigarLineManagement
